import { takeEvery, call, put, select } from 'redux-saga/effects';
import _ from 'lodash';
import axios from '../axiosConfig';
import {
  updateHotelSearchObj,
  hotelSearch,
  hotelSearchFailure,
  hotelSearchSuccess,
} from '../../slices/hotel';
import uniqKeyGenerator from '../../../utils/uniqKeyGenerator';
import { ListPageSize, SearchStatus, TravelTypes } from '../../../constants';
import formatRoomsRequest from '../../../helpers/formatRoomsRequest';
import { changeTrip } from '../../slices/trip';
import getHotelPriceDisplayPreference from '../../../helpers/selectors/getHotelPriceDisplayPreference';
import generateUniqueId from '../../../utils/generateUniqueId';
import checkDoShowMarkupRates from '../../../helpers/selectors/checkDoShowMarkupRates';

const queryString = require('query-string');

function* searchHotelsWorker(action) {
  try {
    const { ignoreCachedResultsForModes } = yield select((store) => store.trip);
    const { currencyCode } = yield select((store) => store.tripSearch);
    const hotelRateCodes = yield select((store) => store.user.hotelRateCodes);
    const rateDisplayPreference = yield select(getHotelPriceDisplayPreference(true));
    const {
      searchObj: searchObjOld,
      searchProgress: storeSearchProgress,
      doHaveMoreHotels,
      requestId,
    } = yield select((store) => store.hotel.hotelSearches[action.payload.tripId] || {});
    const doConsiderMarkupRate = yield select(
      checkDoShowMarkupRates({
        mode: TravelTypes.hotel,
        tripId: action.payload.tripId,
      }),
    );

    const { hasUserInteractedWithHotelList } = yield select((store) => store.hotel);
    let rateCodes =
      (action.payload && action.payload.searchObj && action.payload.searchObj.rateCodes) ||
      (searchObjOld && searchObjOld.rateCodes);
    if (hotelRateCodes && hotelRateCodes.length && !rateCodes) {
      rateCodes = _.map(
        _.filter(hotelRateCodes, (r) => r.isSelectedByDefault),
        'rateCode',
      );
      if (rateCodes.length === 0) rateCodes = undefined;
    }

    const searchStatus =
      (!action.payload.doGetRemainingHotelsOnly && !action.payload.doIgnoreStoredResults) ||
      action.payload.doShowProgressbar
        ? SearchStatus.SEARCHING
        : undefined;

    let searchProgress;
    if (action.payload.searchProgress) {
      searchProgress = action.payload.searchProgress;
    } else if (doHaveMoreHotels || action.payload.doIgnoreStoredResults) {
      searchProgress = storeSearchProgress;
    }
    yield put(
      updateHotelSearchObj({
        ...{
          ...action.payload,
          searchObj: {
            ...(action.payload.searchObj || {}),
            rateCodes,
          },
        },
        searchStatus,
        searchProgress,
        requestId: action.payload.doGetRemainingHotelsOnly ? action.payload.requestId : requestId,
      }),
    );

    const { searchObj, selectedAddress } = yield select(
      (store) => store.hotel.hotelSearches[action.payload.tripId] || {},
    );
    const { staterooms } = yield select((store) => store.tripSearch.searchObj);
    const query = {
      ...searchObj,
      filterAmenities: searchObj.filterAmenities
        ? searchObj.filterAmenities.map((item) => item.value)
        : [],
      filterHotelChains: searchObj.filterHotelChains || [],
      filterHotelKeys: searchObj.filterHotelKeys
        ? searchObj.filterHotelKeys.map((item) => item.value)
        : [],
      placeId: selectedAddress && !selectedAddress.hotelId ? selectedAddress.value : undefined,
      latitude:
        selectedAddress && selectedAddress.hotelId ? selectedAddress.lat : searchObj.latitude,
      longitude:
        selectedAddress && selectedAddress.hotelId ? selectedAddress.lon : searchObj.longitude,
      ...formatRoomsRequest(staterooms),
      tripId: action.payload.doGetRemainingHotelsOnly ? action.payload.tripId : undefined,
      doGetRemainingHotelsOnly: action.payload && action.payload.doGetRemainingHotelsOnly,
      requestId: action.payload && action.payload.requestId,
      preferredHotelId: selectedAddress ? selectedAddress.hotelId : searchObj.preferredHotelId,
      airportGeoId: !selectedAddress && searchObj.airportGeoId ? searchObj.airportGeoId : undefined,
      airportGeoFeatureCode:
        !selectedAddress && searchObj.airportGeoFeatureCode
          ? searchObj.airportGeoFeatureCode
          : undefined,
      doIgnoreCachedResults:
        ignoreCachedResultsForModes &&
        ignoreCachedResultsForModes.length &&
        ignoreCachedResultsForModes.indexOf(TravelTypes.hotel) > -1
          ? true
          : undefined,
      rateDisplayPreference,
      currencyCode: currencyCode || undefined,
      doGetCommissionableRatesOnly: searchObj.doShowCommissionableRatesOnly,
      showRefundableHotelRatesOnly: searchObj.showRefundableHotelRatesOnly,
      doConsiderMarkupRate,
      hotelProvidersToInclude: searchObj.hotelProvidersToInclude,
    };
    if (!selectedAddress && searchObj.placeId) query.placeId = searchObj.placeId;
    if (query.doIgnoreCachedResults)
      yield put(
        changeTrip({
          field: 'ignoreCachedResultsForModes',
          value: _.reject(ignoreCachedResultsForModes, (ss) => ss === TravelTypes.hotel),
        }),
      );

    const qs = queryString.stringify(query, { skipNull: true });
    const responses = yield select((store) => store.hotel.hotelSearchResponses);
    let responseData = responses[qs];

    let doMergeCached = hasUserInteractedWithHotelList ? false : undefined;
    if (action.payload && action.payload.searchObj && action.payload.searchObj.hasFilterApplied) {
      doMergeCached = true;
    }
    const url = `/hotels/search?${queryString.stringify({ ...query, doMergeCached }, { skipNull: true })}`;
    const config = {
      method: 'get',
      url,
    };
    const { data } = yield call(axios, config);
    // TODO put in request unique ID for response date
    uniqKeyGenerator(data.hotels);
    responseData = data;

    if (
      data &&
      data.doHaveMoreHotels &&
      searchObj &&
      (action.payload.defaultSearchObj || !hasUserInteractedWithHotelList) &&
      !action.payload.doIgnoreStoredResults &&
      searchObj.pageSize === ListPageSize
    ) {
      // Call hotel search to get remaining hotels only
      yield put(
        hotelSearch({
          tripId: action.payload.tripId,
          doGetRemainingHotelsOnly: true,
          searchProgress: 10,
          requestId: generateUniqueId(),
          previousRequestId: requestId,
        }),
      );
    }
    if (action.payload.doGetRemainingHotelsOnly) {
      try {
        // Handled case: When new polling started, and previous polling/search stil in progress,
        // it hide progressbar for sometime
        const { requestId: currentRequestId } = yield select(
          (store) => store.hotel.hotelSearches[action.payload.tripId] || {},
        );
        if (action.payload.requestId === currentRequestId) {
          // Hide hotel search progressbar
          yield put(
            updateHotelSearchObj({
              tripId: action.payload.tripId,
              searchProgress: undefined,
            }),
          );
        }
      } catch (e) {
        console.log(e);
      }
    }

    if (!action.payload.doGetRemainingHotelsOnly) {
      const isSelectedAddress =
        responseData.hotels[0] && !responseData.hotels[0].searchGeoname.departureCityId;
      yield put(
        hotelSearchSuccess({
          isSelectedAddress,
          queryString: qs,
          response: responseData,
          tripId: action.payload.tripId,
          amenitiesResults: responseData.filters.amenities,
          hotelChainsResults: responseData.filters.hotelChains,
          hotelResults: responseData.filters.hotels,
          minPrice: responseData.filters.minPrice,
          maxPrice: responseData.filters.maxPrice,
          currencyCode: responseData.filters.currencyCode,
          doHaveMoreHotels: responseData.doHaveMoreHotels,
        }),
      );
    }
  } catch (error) {
    yield put(hotelSearchFailure(error));
  }
}

function* searchHotelsWatcher() {
  yield takeEvery(hotelSearch.toString(), searchHotelsWorker);
}

export default searchHotelsWatcher;
